import type {
  DueRowPublic,
  DueStatus,
  DueWindow,
  ExcludedItemPublic,
  FindingKind,
  ForecastExclusionPublic,
  ForecastItemPublic,
  ForecastResultPublic,
  LastDonePublic,
  Unit,
} from "@common/client"
import { formatShortDate } from "@common/lib/date-utils"
import { formatCount, formatDecimal, formatFixed } from "@common/lib/number"
import type { SortValue } from "@/routes/_layout/maintenance-programmes/-lib/sort"
import {
  intervalText,
  UNIT_ABBREVIATIONS,
} from "@/routes/_layout/maintenance-programmes_/$programmeId/-lib/schedule"
import type { Column } from "./columns"

export type ForecastRow = DueRowPublic | ForecastItemPublic | ExcludedItemPublic

export type RowStatus = DueStatus | "undetermined" | "excluded"

export type RowWindow = DueWindow | "undetermined" | "excluded"

export const APPLICABILITIES = ["applicable", "unknown"] as const

export type Applicability = (typeof APPLICABILITIES)[number]

export type RowApplicability = Applicability | "excluded"

type CountedUnit = Extract<Unit, "hours" | "cycles" | "landings">

export const STATUS_LABELS: Record<RowStatus, string> = {
  overdue: "Overdue",
  in_tolerance: "In tolerance",
  due_soon: "Due soon",
  scheduled: "Scheduled",
  undetermined: "Undetermined",
  excluded: "Excluded",
}

export const APPLICABILITY_LABELS: Record<Applicability, string> = {
  applicable: "Applicable",
  unknown: "Unknown",
}

export const UNKNOWN_APPLICABILITY_NOTE =
  "The automatic applicability check could not decide whether this task applies."

export const WINDOWS: RowWindow[] = [
  "overdue",
  "next_7_days",
  "days_8_to_30",
  "days_31_to_90",
  "days_91_to_180",
  "days_181_to_365",
  "later",
  "undetermined",
  "excluded",
]

export const WINDOW_LABELS: Record<RowWindow, string> = {
  overdue: "Overdue",
  next_7_days: "Next 7 days",
  days_8_to_30: "8–30 days",
  days_31_to_90: "31–90 days",
  days_91_to_180: "91–180 days",
  days_181_to_365: "181–365 days",
  later: "Later",
  undetermined: "Undetermined",
  excluded: "Excluded",
}

export const FINDING_LABELS: Record<FindingKind, string> = {
  applicability_unknown: "Applicability unknown",
  condition_unknown: "Condition unknown",
  option_not_chosen: "Operator option not chosen",
  event: "Due on an event",
  external: "Set by an outside document",
  no_subject: "Not fitted",
  subject_unknown: "Component unknown",
  no_counter: "No counter reading",
  no_rate: "No utilisation rate",
  too_far_to_project: "Too far to project",
  no_start_date: "No start date",
  no_record: "No last-done record",
  no_case: "No schedule applies",
}

export function dueOf(row: ForecastRow): DueRowPublic | null {
  return "due_on" in row ? row : null
}

export function exclusionOf(row: ForecastRow): ForecastExclusionPublic | null {
  return "exclusion" in row ? row.exclusion : null
}

export function keyOf(row: ForecastRow): string {
  return `${row.aircraft_id}.${row.task_id ?? row.check_id}`
}

export function forAircraft(
  result: ForecastResultPublic,
  aircraftId: string,
): ForecastResultPublic {
  const owned = (row: { aircraft_id: string }) => row.aircraft_id === aircraftId
  return {
    ...result,
    aircraft: result.aircraft.filter(owned),
    rows: result.rows.filter(owned),
    undetermined: result.undetermined.filter(owned),
    excluded: result.excluded.filter(owned),
  }
}

export function rowsOf(result: ForecastResultPublic): ForecastRow[] {
  return [...result.rows, ...result.undetermined, ...result.excluded]
}

export function statusOf(row: ForecastRow): RowStatus {
  if (exclusionOf(row)) return "excluded"
  return dueOf(row)?.status ?? "undetermined"
}

export function windowOf(row: ForecastRow): RowWindow {
  if (exclusionOf(row)) return "excluded"
  return dueOf(row)?.window ?? "undetermined"
}

export function applicabilityOf(row: ForecastRow): RowApplicability {
  if (exclusionOf(row)) return "excluded"
  const unknown = row.findings.some(
    (finding) => finding.kind === "applicability_unknown",
  )
  return unknown ? "unknown" : "applicable"
}

function isCounted(unit: Unit): unit is CountedUnit {
  return unit === "hours" || unit === "cycles" || unit === "landings"
}

function counterText(value: string, unit: Unit): string {
  const amount = Number(value)
  const shown = unit === "hours" ? formatFixed(amount, 1) : formatCount(amount)
  return `${shown} ${UNIT_ABBREVIATIONS[unit]}`
}

export function typeText(row: ForecastRow): string {
  if (row.task_code) return row.task_code.toUpperCase()
  return row.kind === "check" ? "Check" : ""
}

export function intervalOf(row: ForecastRow): string {
  if (row.schedule) return intervalText(row.schedule)
  const due = dueOf(row)
  return due
    ? `${formatDecimal(due.interval, 3)} ${UNIT_ABBREVIATIONS[due.unit]}`
    : ""
}

type Times = Pick<LastDonePublic, "hours" | "cycles" | "landings">

function counterOf(times: Times, unit: CountedUnit): string | null {
  switch (unit) {
    case "hours":
      return times.hours
    case "cycles":
      return times.cycles
    case "landings":
      return times.landings
  }
}

export function timesText(times: Times): string {
  const units: CountedUnit[] = ["hours", "cycles", "landings"]
  return units
    .flatMap((unit) => {
      const value = counterOf(times, unit)
      return value === null ? [] : [counterText(value, unit)]
    })
    .join(" · ")
}

export function lastDoneText(row: ForecastRow): string {
  const done = row.last_done
  if (!done) return ""
  const unit = dueOf(row)?.unit
  const preferred = unit && isCounted(unit) ? unit : "hours"
  const value = counterOf(done, preferred)
  return [
    formatShortDate(done.performed_on),
    value === null ? null : counterText(value, preferred),
  ]
    .filter(Boolean)
    .join(" · ")
}

export function nextDueText(due: DueRowPublic): string {
  return due.due_at
    ? counterText(due.due_at, due.unit)
    : formatShortDate(due.due_on)
}

export function inText(due: DueRowPublic): string {
  if (due.days < 0) return `${-due.days} d over`
  return due.days === 0 ? "Today" : `${due.days} d`
}

export function remainingText(due: DueRowPublic): string {
  const amount = Number(due.remaining)
  const size = formatCount(Math.abs(amount))
  const unit = isCounted(due.unit) ? UNIT_ABBREVIATIONS[due.unit] : "d"
  return amount < 0 ? `${size} ${unit} over` : `${size} ${unit}`
}

export function findingText(row: ForecastRow): string {
  const [first] = row.findings
  return first ? FINDING_LABELS[first.kind] : ""
}

export function sortValue(row: ForecastRow, column: Column): SortValue {
  const due = dueOf(row)
  switch (column) {
    case "aircraft":
      return row.registration
    case "number":
      return row.number ?? row.title
    case "title":
      return row.title
    case "type":
      return typeText(row) || null
    case "programme":
      return row.programme_title
    case "interval":
      return due ? `${due.unit} ${Number(due.interval)}` : null
    case "last_done":
      return row.last_done?.performed_on ?? null
    case "next_due":
    case "due":
    case "in":
      return due ? due.days : null
    case "remaining":
      return due ? Number(due.remaining) : null
    case "man_hours":
      return row.man_hours === null ? null : Number(row.man_hours)
  }
}
